import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@mui/material/Button";
import CardsList from "@/components/CardsList";
import EmptyData from "@/components/EmptyData";
import MainActions from "@/components/MainActions";
import {
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
} from "recharts";
import Rating from "@/components/Rating";
import Tabs from "@/components/Tabs";
import ViewField from "@/components/Input/ViewField";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";
import HandleAttendanceModal from "@/pages/Manager/Match/handleAttendance";
import HandleCopyExercisesModal from "@/pages/Manager/Match/handleCopyExercises";
import HandleRPEModal from "@/pages/Manager/Match/handleRPE";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import {AttachmentAPI} from "@/api/attachment";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {TrainingAPI} from "@/api/training";

import {setTrainingTab} from "@/actions";

import {phasesOptions} from "@/pages/Manager/Match/handleExercise";
import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	attendanceStatusOptions,
	rpeOptions,
} from "@/utils/constants";
import {getAttachment, stdev} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";
import {generateTrainingPDF} from "@/utils/generateTrainingPDF";

import "./index.scss";

const COLORS = [
	"#8884d8",
	"#82ca9d",
	"#ffc658",
	"#ff8042",
	"#8dd1e1",
	"#a4de6c",
	"#d0ed57",
];

function TrainingPage(props) {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const currentTab = useSelector((state) => state.trainingTab);
	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
	});

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [trainingData, setTrainingData] = useState({});
	const [teamPlayers, setTeamPlayers] = useState([]);
	const [attachments, setAttachments] = useState([]);

	const [formData, setFormData] = useState({});

	const [openAttendanceModal, setOpenAttendanceModal] = useState(false);
	const [openCopyExercisesModal, setOpenCopyExercisesModal] = useState(false);
	const [openRPEModal, setOpenRPEModal] = useState(false);

	const [initialData, setInitialData] = useState();
	const [entityToDelete, setEntityToDelete] = useState({});

	const [attendance, setAttendance] = useState("");

	const getTraining = async () => {
		setLoading(true);
		try {
			const response = await TrainingAPI.getById(id);
			setTrainingData(response);

			if (userRole === "Player") {
				const playerMeasurement =
					response.trainingRPE?.filter((ps) => ps.playerId === playerId)?.[0] ||
					{};
				setFormData({
					...Object.assign({}, playerMeasurement),
					rpe: parseInt(playerMeasurement.rpe),
				});

				const playerConvocation = response.trainingConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				setAttendance(playerConvocation ? playerConvocation.status : undefined);
				setLoading(false);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getTeamPlayers = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.list({prospect: false, seasonId});
			setTeamPlayers(response?.filter((p) => p.status === "Interno"));
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getTrainingAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("training", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({seasonId, prospect: false});
			setInternalTeams(response);
		} catch (error) {
			console.log(error);
		}
	};
	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleAttendance = async (data, status, type) => {
		setLoading(true);
		try {
			const body = playerId
				? data
				: type === "multiple"
					? data.player?.map((p) => {
							return {
								trainingId: trainingData?.id,
								playerId: p.value,
								status,
							};
						})
					: {
							trainingId: trainingData?.id,
							playerId: data.player?.value,
							status,
						};

			delete body.player;

			type === "multiple"
				? await TrainingAPI.createMultipleAttendances(body)
				: await TrainingAPI.createAttendance(body);
			getTraining();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handlePlayerAttendance = async (data, attendanceId) => {
		setLoading(true);
		try {
			attendanceId
				? await TrainingAPI.updateAttendance(id, attendanceId, data)
				: await TrainingAPI.createAttendance(data);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleMeasurement = async (data, type, id) => {
		setLoading(true);
		try {
			if (data.rpe) {
				const rpe =
					userRole === "Player"
						? data.rpe || undefined
						: data.rpe.value || undefined;

				const playerMeasurement = playerId
					? trainingData.trainingRPE?.filter(
							(ps) => ps.playerId === playerId,
						)?.[0]
					: {};

				const body = {
					id: userRole === "Player" ? playerMeasurement?.id : id,
					trainingId: trainingData?.id,
					playerId: userRole === "Player" ? playerId : data.player.value,
					...data,
					rpe,
				};
				delete body.player;

				playerMeasurement?.id || id
					? await TrainingAPI.updateMeasurement(
							trainingData?.id,
							playerMeasurement?.id || id,
							body,
						)
					: await TrainingAPI.createMeasurement(body);
			}

			if (type === "multiple") {
				const body = [];
				Object.keys(data?.players)?.forEach((pk) => {
					if (data.players[pk]?.rpe?.value) {
						body.push({
							trainingId: trainingData?.id,
							playerId: data.players[pk]?.player?.value,
							rpe: data.players[pk]?.rpe?.value,
						});
					}
				});

				await TrainingAPI.createMeasurements(body);
			}

			if (userRole === "Player") {
				const playerConvocation = trainingData?.trainingConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				await handlePlayerAttendance(
					{
						id: playerConvocation?.id,
						trainingId: trainingData?.id,
						playerId,
						status: data.rpe ? attendanceStatusOptions[0].label : attendance,
					},
					playerConvocation?.id,
				);
				navigate("/trainings");
			} else {
				getTraining();
				handleClose();
			}
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteTraining = async () => {
		setLoading(true);
		try {
			await TrainingAPI.delete(trainingData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/trainings");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		if (entityToDelete?.id) {
			try {
				await AttachmentAPI.delete(entityToDelete?.id);
				handleNotification(SUCCESSFUL_OPERATION, "success");
				setOpenHandleConfirm(false);
				getTrainingAttachment();
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		} else {
			return;
		}
	};

	const deleteAttendance = async () => {
		setLoading(true);
		try {
			await TrainingAPI.deleteAttendance(trainingData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getTraining();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteMeasurement = async () => {
		setLoading(true);
		try {
			await TrainingAPI.deleteMeasurement(trainingData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getTraining();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setOpenAttendanceModal(false);
		setOpenRPEModal(false);
		setInitialData();
	};

	useEffect(() => {
		getTraining();
		getTrainingAttachment();
	}, [id]);

	const [trainingExercises, setTrainingExercises] = useState([]);
	const [trainingChartData, setTrainingChartData] = useState([]);
	const getTrainingExercises = async (resetExercises = false) => {
		if (resetExercises) setLoading(true);
		try {
			if (resetExercises) {
				const promises = [];
				trainingExercises
					.filter((e) => e.id !== entityToDelete?.id)
					?.forEach((e, s) => {
						promises.push(
							TrainingAPI.updateDetail(trainingData?.id, e.id, {
								...e,
								sorting: s + 1,
							}),
						);
					});

				await Promise.all(promises);
			}

			const response = await TrainingAPI.getDetails(id);
			setTrainingExercises(response);

			setTrainingChartData(
				Object.values(
					response.reduce((acc, item) => {
						const type = item.exercise?.exerciseType || "Sconosciuto";
						const duration = item.duration || 0;

						if (!acc[type]) {
							acc[type] = {name: type, value: 0};
						}

						acc[type].value += duration;
						return acc;
					}, {}),
				),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleCopyExercises = async (data) => {
		const promises = [];
		trainingExercises.forEach((te) => {
			const body = {...te, trainingId: data.training?.value};
			promises.push(TrainingAPI.createDetail(body));
		});

		setLoading(true);
		try {
			await Promise.all(promises);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setLoading(false);
			setOpenCopyExercisesModal(false);
			navigate("/trainings/" + data.training?.value);
		} catch (error) {
			console.log(error);
			setLoading(false);
			setOpenCopyExercisesModal(false);
		}
	};

	useEffect(() => {
		if (currentTab === "training_attendance_players") {
			getTeamPlayers(false);
		}

		if (currentTab === "rpe") {
			getTeamPlayers(false);
		}

		if (currentTab === "main_info" || currentTab === "training_exercises") {
			getTrainingExercises();
		}
	}, [currentTab]);

	const getRPEAvg = () => {
		let sum = 0;
		let counter = 0;
		trainingData?.trainingRPE?.forEach((r) => {
			const currentPlayer = teamPlayers?.filter(
				(p) => p.id === r.playerId,
			)?.[0];

			if (currentPlayer && !currentPlayer.goalkeeper) {
				sum = sum + parseInt(r.rpe);
				counter = counter + 1;
			}
		});

		return counter ? sum / counter : 0;
	};

	const mainConfiguration = [
		{
			value:
				trainingData?.startDate &&
				moment(trainingData?.startDate).format("DD/MM/YYYY"),
			label: t("date_label"),
			size: "col-6",
		},
		{
			value: trainingData?.startDate
				? moment(trainingData?.startDate).format("HH:mm") +
					" - " +
					(trainingData?.endDate
						? moment(trainingData?.endDate).format("HH:mm")
						: "---")
				: "---",
			label: t("time_label"),
			size: "col-6",
		},
		{
			value: trainingData?.playersCategory,
			label: t("category"),
			size: "col-6",
		},
		{
			value: trainingData?.fieldName,
			label: "Campo",
			size: "col-6",
		},
		{
			value: trainingData.trainingConvocations?.filter(
				(c) => c.status === "Presente",
			).length,
			label: t("appearances_number"),
			size: "col-6",
		},
		{
			value: trainingData?.trainingDetails?.length,
			label: t("exercises_number"),
			size: "col-6",
		},
		{
			value: trainingData?.trainingRPE?.length,
			label: "N° RPE",
			size: "col-6",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/trainings"),
			color: "info",
		},
	];

	// Aggiungi pulsante download PDF (solo per ruoli non Player)
	if (userRole !== "Player" && trainingData?.id) {
		mainActions.push({
			icon: <PictureAsPdfIcon />,
			title: "Scarica PDF",
			onClick: () => {
				try {
					generateTrainingPDF(trainingData, teamPlayers, trainingExercises, t);
					handleNotification("PDF generato con successo", "success");
				} catch (error) {
					console.error("Errore nella generazione del PDF:", error);
					handleNotification("Errore nella generazione del PDF", "error");
				}
			},
			variant: "outlined",
			color: "error",
		});
	}

	if (currentTab === "rpe" && userRole !== "Player") {
		mainActions.push(
			{
				icon: <AddIcon />,
				onClick: () => {
					setInitialData({
						matchCategoryId: trainingData?.playersCategoryId,
						matchCategoryLabel: trainingData?.playersCategory,
					});
					setOpenRPEModal(true);
				},
			},
			{
				icon: <DeleteIcon />,
				title: "Elimina",
				onClick: () => {
					setEntityToDelete({type: "training", id});
					setOpenHandleConfirm(true);
				},
				color: "error",
			},
		);
	}

	if (currentTab === "training_exercises") {
		mainActions.push(
			{
				variant: "outlined",
				color: "primary",
				icon: <ContentCopyIcon color="primary" />,
				onClick: () => {
					setOpenCopyExercisesModal(true);
				},
				disabled: !trainingExercises?.length,
			},
			{
				icon: <EditIcon />,
				onClick: () => {
					navigate("/trainings/" + id + "/exercises");
				},
			},
		);
	}

	if (currentTab === "rpe" && userRole === "Player") {
		mainActions.push({
			icon: <SaveIcon />,
			title: "Salva",
			onClick: () => handleMeasurement(formData, formData?.id),
			disabled: !formData.rpe && !attendance,
		});
	}

	const breadcrumbsElements = [
		{label: "Allenamenti", path: "/trainings"},
		{label: "/"},
	];

	const trainingCategory = categories?.filter(
		(c) => c.id === trainingData?.playersCategoryId,
	)?.[0];
	const hasRPE = !trainingCategory?.isBasicActivities;

	// const presents = trainingData.trainingConvocations?.filter(
	// 	(c) => c.status === "Presente",
	// );

	const getTeamPlayersConfiguration = (status) => ({
		data: trainingData?.trainingConvocations
			?.filter((c) => c.status === status)
			?.map((c) => {
				const currentPlayer = teamPlayers?.find((p) => p.id === c.playerId);

				if (currentPlayer) {
					currentPlayer.convocationId = c.id;
				}
				return currentPlayer;
			})
			?.filter(Boolean)
			?.sort((a, b) => a.lastName?.localeCompare(b.lastName)),
		handleDelete: (element) => {
			setEntityToDelete({
				type: "attendance",
				id: element.convocationId,
			});
			setOpenHandleConfirm(true);
		},
	});

	const playersConvocationsTypes = [
		{label: "Presenti", status: "Presente"},
		{label: "Assenti", status: "Assente"},
		{label: "Infortunati", status: "Infortunato"},
	];

	const tabs = [
		{
			key: "main_info",
			label: t("informations"),
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								content={
									<div className="row">
										<div className="col-12 col-lg-6">
											<ViewField configuration={mainConfiguration} />
										</div>
										<div className="col-12 col-lg-6">
											<div className="view-field-container mb-0">
												<div className="label">Tipologie esercizi</div>
											</div>
											<div className="d-flex align-items-center justify-content-center">
												{trainingExercises?.length ? (
													<ResponsiveContainer width="50%" aspect={1}>
														<PieChart
															margin={{top: 0, right: 0, bottom: 0, left: 0}}
														>
															<Pie
																data={trainingChartData}
																dataKey="value"
																nameKey="name"
																cx="50%"
																cy="50%"
																outerRadius="80%"
															>
																{trainingChartData.map((entry, index) => (
																	<Cell
																		key={`cell-${index}`}
																		fill={COLORS[index % COLORS.length]}
																	/>
																))}
															</Pie>
															<Tooltip />
															<Legend />
														</PieChart>
													</ResponsiveContainer>
												) : (
													<EmptyData />
												)}
											</div>
										</div>
										{/* <div className="col-12">
											{hasRPE &&
											presents?.length &&
											presents.length > trainingData.trainingRPE?.length ? (
												<div className="row">
													<div className="col-12">
														<div className="alert-container d-flex align-items-center gap-1">
															<span>
																Nell'allenamento potrebbero mancare alcuni RPE:
																il numero di presenze è differente rispetto agli
																RPE registrati.
															</span>
														</div>
													</div>
												</div>
											) : null}
										</div> */}
									</div>
								}
								openable={false}
							/>
						</div>
						{userRole !== "Player" ? (
							<div className="col-sm-12 padding-small mt-2">
								<VLFCard
									title="Allegati"
									content={
										<AttachmentsContainer
											entityId={id}
											entityType="training"
											attachments={attachments}
											handleAdd={() => getTrainingAttachment()}
											handleDownload={handleDownload}
											handleDelete={(id) => {
												setEntityToDelete({type: "attachment", id});
												setOpenHandleConfirm(true);
											}}
											handleNotification={handleNotification}
										/>
									}
								/>
							</div>
						) : null}
					</div>
				);
			},
		},
		{
			key: "training_attendance_players",
			label: t("appearances"),
			hidden: userRole === "Player",
			render: () => {
				return (
					<div className="row no-margin g-1">
						{playersConvocationsTypes?.map((t, k) => {
							return (
								<div key={k} className="col-sm-12 padding-small">
									<div className="block-title-container d-flex align-items-center justify-content-between">
										<div className="block-title f-bold padding-small">
											{t.label}
										</div>
										<Button
											variant="contained"
											onClick={() => {
												setInitialData({
													matchCategoryId: trainingData?.playersCategoryId,
													matchCategoryLabel: trainingData?.playersCategory,
													status: t.status,
												});
												setOpenAttendanceModal(true);
											}}
											size="small"
										>
											<div className="d-flex align-items-center">
												<AddIcon />
											</div>
										</Button>
									</div>
									<div className="block-items-container">
										<CardsList
											type="roster_player"
											listConfiguration={getTeamPlayersConfiguration(t.status)}
										/>
									</div>
								</div>
							);
						})}
					</div>
				);
			},
		},
		{
			key: "training_exercises",
			label: t("exercises"),
			hidden: userRole === "Player",
			render: () => {
				return (
					<div className="row no-margin">
						{trainingExercises
							?.sort((a, b) => {
								if (a.phase !== b.phase) return a.phase - b.phase;
								return a.sorting - b.sorting;
							})
							?.map((te, k, arr) => {
								const showTitle = k === 0 || te.phase !== arr[k - 1].phase;

								return (
									<div key={k} className="col-12 padding-small mt-2">
										{showTitle && (
											<div className="f-bold px-1">
												{
													phasesOptions?.find(
														(p) => p.value === parseInt(te.phase),
													)?.label
												}
											</div>
										)}

										<div className="exercise-container">
											<div className="exercise-counter">
												{te.sorting ? te.sorting : "---"}
											</div>
											<div className="exercise-text-container">
												<div
													className="d-flex align-items-center justify-content-between py-1"
													style={{borderBottom: "2px solid #ebf1fd"}}
												>
													<div className="exercise-title-container">
														{/* <img
															src={require("@/assets/images/manager/exercise.png")}
															alt=""
														/> */}
														<div className="exercise-title">
															{te.exercise?.name}
														</div>
													</div>
													<div
														className="d-flex align-items-center"
														style={{gap: "15px"}}
													>
														<div className="exercise-category">
															{te.exercise?.exerciseType}
														</div>
														<div className="exercise-duration">
															<img
																src={require("@/assets/images/manager/time.png")}
																alt=""
															/>
															<div className="exercise-duration-text">
																{te.duration ? te.duration + "'" : "---"}
															</div>
														</div>
													</div>
												</div>
												<div className="exercise-notes">
													{te.exercise?.imageUrl && (
														<img src={te.exercise?.imageUrl} alt="" />
													)}

													<VLFTextField
														value={
															te?.description?.length
																? te.description
																: "Nessuna descrizione inserita"
														}
														size="small"
														multiline
														minRows={1}
														readOnly
													/>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						{!trainingExercises?.length && <EmptyData />}
					</div>
				);
			},
		},
		{
			key: "rpe",
			label: "RPE",
			hidden: !hasRPE,
			render: () => {
				return (
					<div className="row no-margin rpe-tab-container g-2">
						{userRole === "Player" ? (
							[
								<div
									key="convocation"
									className="col-sm-12 col-lg-6 padding-small"
								>
									<div className="player-choice d-flex flex-column align-items-center justify-content-center h-100">
										<div className="choice-label">
											Hai partecipato all'allenamento?
										</div>
										<div className="choice-options">
											{attendanceStatusOptions.map((as, k) => {
												return (
													<div
														key={k}
														className={
															"single-option" +
															(attendance === as.value ? " selected" : "")
														}
														onClick={() => setAttendance(as.value)}
													>
														{as.label}
													</div>
												);
											})}
										</div>
									</div>
								</div>,
								<div key="rpe" className="col-sm-12 col-lg-6 padding-small">
									<div
										className="player-choice d-flex flex-column align-items-center justify-content-center h-100"
										style={{lineHeight: 1}}
									>
										<div className="choice-label">RPE</div>
										<Rating
											selectedRating={formData.rpe}
											setSelectedRating={(value) => {
												setFormData({...formData, rpe: value});
											}}
											max={10}
											min={1}
										/>
										<div>
											{
												rpeOptions.filter(
													(o) => o.value === formData.rpe?.toString(),
												)?.[0]?.label
											}
										</div>
									</div>
								</div>,
							]
						) : (
							<>
								<div className="col-sm-12 padding-small">
									<div className="rpe-team-container">
										<div className="kpi-container">
											<div className="kpi-value">{getRPEAvg()}</div>
											<div className="kpi-label">
												{"Media (portieri esclusi)"}
											</div>
										</div>
										<div className="kpi-container">
											<div className="kpi-value">
												{stdev(
													trainingData?.trainingRPE?.map((r) =>
														parseInt(r.rpe),
													),
												)}
											</div>
											<div className="kpi-label">{"Deviazione standard"}</div>
										</div>
									</div>
								</div>
								{!trainingData?.trainingRPE?.length && <EmptyData />}
								{trainingData?.trainingRPE
									?.sort((a, b) => {
										const playerA = teamPlayers.find(
											(player) => player.id === a.playerId,
										);
										const playerB = teamPlayers.find(
											(player) => player.id === b.playerId,
										);

										const nameA = playerA?.lastName.toLowerCase();
										const nameB = playerB?.lastName.toLowerCase();

										if (nameA < nameB) return -1;
										if (nameA > nameB) return 1;
										return 0;
									})
									?.map((mr, k) => {
										const currentPlayer = teamPlayers.filter(
											(p) => p.id === mr.playerId,
										)?.[0];

										return (
											<div key={k} className="col-sm-12 padding-small">
												<div className="rpe-value-container">
													<div className="icon">
														<img
															src={require("@/assets/images/coach/rpe.png")}
															alt=""
														/>
													</div>
													<div className="player-rpe-container">
														<div className="player-info-container">
															<div className="player-container">
																<div
																	className="player clickable"
																	onClick={() =>
																		navigate("/players/" + mr.playerId)
																	}
																>
																	{currentPlayer
																		? currentPlayer.firstName +
																			" " +
																			currentPlayer.lastName
																		: "---"}
																</div>
															</div>
															<div className="player-container">
																{currentPlayer?.primaryRole}
															</div>
														</div>
														<div className="value">
															{
																rpeOptions.filter(
																	(o) => o.value === mr.rpe,
																)?.[0]?.label
															}
														</div>
													</div>
													<div className="actions-container">
														<div
															className="action"
															onClick={() => {
																setInitialData({
																	matchCategoryId:
																		trainingData?.playersCategoryId,
																	matchCategoryLabel:
																		trainingData?.playersCategory,
																	...mr,
																});
																setOpenRPEModal(true);
															}}
														>
															<EditIcon />
														</div>
														<div
															className="action"
															onClick={() => {
																setEntityToDelete({
																	type: "rpe",
																	id: mr.id,
																});
																setOpenHandleConfirm(true);
															}}
														>
															<CloseIcon />
														</div>
													</div>
												</div>
											</div>
										);
									})}
							</>
						)}
					</div>
				);
			},
		},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					trainingData?.startDate
						? moment(trainingData.startDate).format("DD/MM/YYYY HH:mm") +
							" - " +
							moment(trainingData.endDate).format("HH:mm")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			{/* <div className="sheet-container">
				{userRole !== "Player" && (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small mt-2">
							<VLFCard
								title={t("appearances")}
								content={
									<>
										<div className="d-flex align-items-center justify-content-end mb-2">
											<Button
												variant="contained"
												onClick={() => {
													setOpenAttendanceModal(true);
												}}
												size="small"
											>
												<div className="d-flex align-items-center">
													<div>Aggiungi</div>
												</div>
											</Button>
										</div>
										<div className="row">
											{trainingData?.trainingConvocations?.map((mc, k) => {
												const currentPlayer = players.filter(
													(p) => p.id === mc.playerId,
												)?.[0];
												return (
													<div
														key={k}
														className="col-12 col-xl-6 padding-small"
													>
														<div className="d-flex flex-column gap-2">
															<div className="stats-player-container attendance mt-1">
																<div className="d-flex align-items-center gap-2">
																	<img
																		className="player-icon"
																		src={require("@/assets/images/manager/player.png")}
																		alt=""
																	/>
																	<div
																		className="player-name clickable match"
																		onClick={() =>
																			navigate("/players/" + mc.playerId)
																		}
																	>
																		{currentPlayer?.firstName +
																			" " +
																			currentPlayer?.lastName}
																	</div>
																</div>
																<div className="stats-container d-flex align-items-center">
																	<div className="player-stat-container flex-row gap-2">
																		<div className="stat-label">
																			{mc.status || "---"}
																		</div>
																		<div
																			className={
																				"stat-label-icon " +
																				getAttendanceStatusIcon(mc.status)
																			}
																		/>
																	</div>
																</div>
																<div className="d-flex align-items-center gap-2">
																	<Button
																		variant={"outlined"}
																		onClick={() => {
																			setInitialData({
																				id: mc.id,
																				playerId: mc.playerId,
																				status: mc.status,
																			});
																			setOpenAttendanceModal(true);
																		}}
																		size="small"
																	>
																		<div className="action-content d-flex align-items-center">
																			<RefreshIcon />
																		</div>
																	</Button>
																	<Button
																		variant={"outlined"}
																		onClick={() => {
																			setEntityToDelete({
																				type: "attendance",
																				id: mc.id,
																			});
																			setOpenHandleConfirm(true);
																		}}
																		size="small"
																		color="error"
																	>
																		<div className="action-content d-flex align-items-center">
																			<DeleteIcon />
																		</div>
																	</Button>
																</div>
															</div>
														</div>
													</div>
												);
											})}
											{!trainingData?.trainingConvocations?.length && (
												<EmptyData />
											)}
										</div>
									</>
								}
							/>
						</div>
					</div>
				)}
				{userRole !== "Player" && (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small mt-2">
							<VLFCard
								title="Esercizi"
								content={
									<>
										<div className="d-flex align-items-center justify-content-end mb-2 gap-2">
											<Button
												variant="outlined"
												onClick={() => {
													setOpenCopyExercisesModal(true);
												}}
												size="small"
												disabled={!trainingExercises?.length}
											>
												<div className="d-flex align-items-center">
													<div>Copia</div>
												</div>
											</Button>
											<Button
												variant="contained"
												onClick={() => {
													setOpenExerciseModal(true);
												}}
												size="small"
											>
												<div className="d-flex align-items-center">
													<div>Aggiungi</div>
												</div>
											</Button>
										</div>
										<div className="row">
											{trainingExercises
												?.sort((a, b) => a.sorting - b.sorting)
												?.map((te, k) => {
													return (
														<div key={k} className="col-12 padding-small mt-2">
															<div className="exercise-container">
																<div className="exercise-counter">
																	{te.sorting ? te.sorting : "---"}
																</div>
																<div className="exercise-text-container">
																	<div className="exercise-title-container">
																		<img
																			src={require("@/assets/images/manager/exercise.png")}
																			alt=""
																		/>
																		<div className="exercise-title">
																			{te.exercise?.name}
																		</div>
																	</div>
																	<div className="exercise-notes">
																		{te.description}
																	</div>
																</div>
																<div className="exercise-duration">
																	<img
																		src={require("@/assets/images/manager/time.png")}
																		alt=""
																	/>
																	<div className="exercise-duration-text">
																		{te.duration ? te.duration + "'" : "---"}
																	</div>
																</div>
																<div className="d-flex align-items-center gap-2 exercise-actions">
																	<Button
																		variant={"outlined"}
																		onClick={() => {
																			handleExerciseOrder(te, "up");
																		}}
																		size="small"
																		disabled={k <= 0}
																	>
																		<div className="action-content d-flex align-items-center">
																			<ArrowUpwardIcon />
																		</div>
																	</Button>
																	<Button
																		variant={"outlined"}
																		onClick={() => {
																			handleExerciseOrder(te, "down");
																		}}
																		size="small"
																		disabled={k >= trainingExercises.length - 1}
																	>
																		<div className="action-content d-flex align-items-center">
																			<ArrowDownwardIcon />
																		</div>
																	</Button>
																	<Button
																		variant={"outlined"}
																		onClick={() => {
																			setEntityToDelete({
																				type: "exercise",
																				id: te.id,
																			});
																			setOpenHandleConfirm(true);
																		}}
																		size="small"
																		color="error"
																	>
																		<div className="action-content d-flex align-items-center">
																			<DeleteIcon />
																		</div>
																	</Button>
																</div>
															</div>
														</div>
													);
												})}
											{!trainingExercises?.length && <EmptyData />}
										</div>
									</>
								}
							/>
						</div>
					</div>
				)}
				{hasRPE && (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small mt-2">
							<VLFCard
								title={userRole === "Player" ? "" : "RPE"}
								content={
									<>
										{userRole !== "Player" && (
											<div className="d-flex align-items-center justify-content-end mb-2">
												<Button
													variant="contained"
													onClick={() => {
														setOpenRPEModal(true);
													}}
													size="small"
												>
													<div className="d-flex align-items-center">
														<div>Aggiungi</div>
													</div>
												</Button>
											</div>
										)}
										<div className="row col-container">
											{userRole !== "Player" ? (
												trainingData?.trainingRPE?.length ? (
													trainingData.trainingRPE.map((rpe, k) => {
														const currentPlayer = players.filter(
															(p) => p.id === rpe.playerId,
														)?.[0];
														return (
															<div
																key={k}
																className="col-sm-12 col-md-6 col-lg-4 mt-1 padding-small"
															>
																<div className="d-flex flex-column gap-2">
																	<div className="stats-player-container">
																		<div className="d-flex align-items-center gap-2">
																			<img
																				className="player-icon"
																				src={require("@/assets/images/coach/rpe.png")}
																				alt=""
																			/>
																			<div
																				className="player-name clickable"
																				onClick={() =>
																					navigate("/players/" + rpe.playerId)
																				}
																			>
																				{currentPlayer?.firstName +
																					" " +
																					currentPlayer?.lastName}
																			</div>
																		</div>
																		<div className="player-stat-container">
																			<div className="stat-value">
																				{
																					rpeOptions.filter(
																						(o) => o.value === rpe.rpe,
																					)?.[0]?.label
																				}
																			</div>
																		</div>
																		<div
																			className="stats-action clickable"
																			onClick={() => {
																				setEntityToDelete({
																					type: "rpe",
																					id: rpe.id,
																				});
																				setOpenHandleConfirm(true);
																			}}
																		>
																			<DeleteIcon
																				style={{
																					width: "18px",
																					height: "18px",
																				}}
																			/>
																		</div>
																	</div>
																</div>
															</div>
														);
													})
												) : (
													<div className="col-sm-12">
														<EmptyData />
													</div>
												)
											) : null}
											{userRole === "Player"
												? [
														<div
															key="convocation"
															className="col-sm-12 col-lg-6"
														>
															<div className="player-choice d-flex flex-column align-items-center">
																<div className="choice-label">
																	Hai partecipato all'allenamento?
																</div>
																<div className="choice-options">
																	{attendanceStatusOptions.map((as, k) => {
																		return (
																			<div
																				key={k}
																				className={
																					"single-option" +
																					(attendance === as.value
																						? " selected"
																						: "")
																				}
																				onClick={() => setAttendance(as.value)}
																			>
																				{as.label}
																			</div>
																		);
																	})}
																</div>
															</div>
														</div>,
														<div key="rpe" className="col-sm-12 col-lg-6">
															<div className="player-choice d-flex flex-column align-items-center">
																<div className="choice-label">RPE</div>
																<Rating
																	selectedRating={formData.rpe}
																	setSelectedRating={(value) => {
																		setFormData({...formData, rpe: value});
																	}}
																	max={10}
																	min={1}
																/>
																<div>
																	{
																		rpeOptions.filter(
																			(o) =>
																				o.value === formData.rpe?.toString(),
																		)?.[0]?.label
																	}
																</div>
															</div>
														</div>,
													]
												: null}
										</div>
									</>
								}
								openable={false}
							/>
						</div>
					</div>
				)}
			</div> */}
			<Tabs
				tabs={tabs}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setTrainingTab(tab))}
			/>
			{openAttendanceModal ? (
				<HandleAttendanceModal
					open={openAttendanceModal}
					confirm={handleAttendance}
					close={handleClose}
					internalTeams={internalTeams}
					players={teamPlayers?.filter(
						(p) =>
							trainingData?.trainingConvocations
								?.map((c) => c.playerId)
								.indexOf(p.id) === -1,
					)}
					initialData={initialData}
				/>
			) : null}
			{openCopyExercisesModal ? (
				<HandleCopyExercisesModal
					open={openCopyExercisesModal}
					confirm={handleCopyExercises}
					close={() => setOpenCopyExercisesModal(false)}
					trainingCategoryId={trainingData?.playersCategoryId}
				/>
			) : null}
			{openRPEModal ? (
				<HandleRPEModal
					open={openRPEModal}
					confirm={handleMeasurement}
					close={handleClose}
					initialData={initialData}
					internalTeams={internalTeams}
					players={teamPlayers?.filter((p) => {
						return (
							initialData?.id ||
							!trainingData?.trainingRPE?.find((r) => r.playerId === p.id)
						);
					})}
				/>
			) : null}
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					switch (entityToDelete?.type) {
						case "training":
							return deleteTraining();
						case "attachment":
							return deleteAttachment();
						case "attendance":
							return deleteAttendance();
						case "rpe":
							return deleteMeasurement();
						default:
							return;
					}
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
				disabled={loading}
			/>
		</Page>
	);
}

export default TrainingPage;
